import { useCallback, useRef, useState } from "react";
import type { DocumentDraft, DocumentSectionKind } from "../../services/api";
import { describeError, type CanvasSource } from "../canvas/canvasModel";
import {
  addRow,
  applyPlan,
  clarifiedInstruction,
  createRows,
  documentProblem,
  editing,
  failed,
  isDataKind,
  keepCandidate,
  moveRow,
  planQuestion,
  planning,
  readyToCompose,
  removeRow,
  requestSections,
  updateRow,
  withInstruction,
  withKind,
  type DocumentRow,
} from "./documentModel";
import { composeDocument, logDocument, planSection } from "./documentService";

const newId = () => crypto.randomUUID();

export interface DocumentCanvas {
  rows: DocumentRow[];
  /** True once the user has asked to create the document. */
  started: boolean;
  composing: boolean;
  /** The saved document, once composed. */
  draft: DocumentDraft | null;
  /** A canvas-level message: validation, or why composing failed. */
  notice: string;
  setInstruction: (id: string, instruction: string) => void;
  setKind: (id: string, kind: DocumentSectionKind) => void;
  add: (kind: DocumentSectionKind) => void;
  remove: (id: string) => void;
  move: (id: string, offset: -1 | 1) => void;
  /** Plans every data row, then composes once all of them have a chart. */
  create: () => Promise<void>;
  /** Composes again after a failure, without re-planning. */
  compose: () => Promise<void>;
  ask: (id: string) => Promise<void>;
  retry: (id: string) => Promise<void>;
  edit: (id: string) => void;
  chooseOption: (id: string, option: string) => Promise<void>;
  keep: (id: string, index: number) => Promise<void>;
  startOver: () => void;
}

/**
 * Drives the document canvas: when rows are planned and when the document is
 * composed. How each row changes lives in `documentModel`.
 *
 * Every async step checks a generation number, so a reply that arrives after
 * "New canvas" is dropped instead of landing on the new rows.
 */
export function useDocumentCanvas(source: CanvasSource | null, title: string): DocumentCanvas {
  const [rows, setRowsState] = useState<DocumentRow[]>(() => createRows(newId));
  const [started, setStarted] = useState(false);
  const [composing, setComposing] = useState(false);
  const [draft, setDraft] = useState<DocumentDraft | null>(null);
  const [notice, setNotice] = useState("");

  // Mirrors of state read by async steps, which would otherwise see the
  // values from the render that started them.
  const rowsRef = useRef(rows);
  const generation = useRef(0);
  const composingRef = useRef(false);
  const draftRef = useRef<DocumentDraft | null>(null);

  const setRows = useCallback((change: (current: DocumentRow[]) => DocumentRow[]) => {
    rowsRef.current = change(rowsRef.current);
    setRowsState(rowsRef.current);
  }, []);

  const changeRow = useCallback(
    (id: string, change: (row: DocumentRow) => DocumentRow) =>
      setRows((current) => updateRow(current, id, change)),
    [setRows],
  );

  const compose = useCallback(async () => {
    if (!source || composingRef.current || draftRef.current) return;
    const current = rowsRef.current;
    if (!readyToCompose(current)) return;
    const gen = generation.current;
    composingRef.current = true;
    setComposing(true);
    setNotice("");
    try {
      const saved = await composeDocument({
        title: title.trim(),
        subject: "",
        source,
        sections: requestSections(current),
      });
      if (gen !== generation.current) return;
      draftRef.current = saved;
      setDraft(saved);
    } catch (error) {
      if (gen !== generation.current) return;
      setNotice(describeError(error).message);
    } finally {
      if (gen === generation.current) {
        composingRef.current = false;
        setComposing(false);
      }
    }
  }, [source, title]);

  /** Plans one data row; the caller decides whether to compose afterwards. */
  const planRow = useCallback(
    async (id: string, instruction?: string): Promise<void> => {
      const row = rowsRef.current.find((item) => item.id === id);
      if (!source || !row || !isDataKind(row.kind)) return;
      const text = instruction ?? row.instruction;
      const gen = generation.current;
      changeRow(id, (item) => planning(item, text));
      try {
        const plan = await planSection(row.kind, planQuestion(row, text), source);
        if (gen !== generation.current) return;
        changeRow(id, (item) => applyPlan(item, plan));
      } catch (error) {
        if (gen !== generation.current) return;
        changeRow(id, (item) => failed(item, describeError(error)));
      }
    },
    [changeRow, source],
  );

  /** After a single row is resolved, the document composes if it now can. */
  const resolveRow = useCallback(
    async (id: string, instruction?: string) => {
      await planRow(id, instruction);
      if (readyToCompose(rowsRef.current)) await compose();
    },
    [compose, planRow],
  );

  const create = useCallback(async () => {
    const problem = documentProblem(rowsRef.current);
    if (problem) {
      setNotice(problem);
      return;
    }
    if (!source) {
      setNotice("Choose a semantic model first.");
      return;
    }
    setNotice("");
    setStarted(true);
    const pending = rowsRef.current.filter(
      (row) => isDataKind(row.kind) && row.phase !== "planned",
    );
    logDocument("info", "create_started", {
      sections: rowsRef.current.length,
      planning: pending.length,
    });
    await Promise.all(pending.map((row) => planRow(row.id)));
    if (readyToCompose(rowsRef.current)) await compose();
  }, [compose, planRow, source]);

  const keep = useCallback(
    async (id: string, index: number) => {
      changeRow(id, (row) => keepCandidate(row, index));
      if (readyToCompose(rowsRef.current)) await compose();
    },
    [changeRow, compose],
  );

  const startOver = useCallback(() => {
    generation.current += 1;
    composingRef.current = false;
    draftRef.current = null;
    setRows(() => createRows(newId));
    setStarted(false);
    setComposing(false);
    setDraft(null);
    setNotice("");
  }, [setRows]);

  return {
    rows,
    started,
    composing,
    draft,
    notice,
    setInstruction: (id, instruction) => changeRow(id, (row) => withInstruction(row, instruction)),
    setKind: (id, kind) => changeRow(id, (row) => withKind(row, kind)),
    add: (kind) => setRows((current) => addRow(current, kind, newId())),
    remove: (id) => setRows((current) => removeRow(current, id)),
    move: (id, offset) => setRows((current) => moveRow(current, id, offset)),
    create,
    compose,
    ask: (id) => resolveRow(id),
    retry: (id) => resolveRow(id),
    edit: (id) => changeRow(id, editing),
    chooseOption: (id, option) => {
      const row = rowsRef.current.find((item) => item.id === id);
      return row ? resolveRow(id, clarifiedInstruction(row.instruction, option)) : Promise.resolve();
    },
    keep,
    startOver,
  };
}
